import { isPlatformBrowser } from '@angular/common';
import { Component, inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, ToastService } from '@cnt-workspace/data-access';
import { Role } from '@curbnturf/entities';
import { IonContent, IonSpinner } from '@ionic/angular/standalone';

/** Codes already exchanged in this page load (Cognito codes are single-use). */
const exchanged = new Set<string>();

/**
 * The query string as the browser first loaded it. Captured when this module
 * is evaluated (the app bundle loads before Angular boots), because the router's
 * first navigation rewrites the URL through the admin app's
 * LowerCaseUrlSerializer, lower-casing the code, state and error text. The
 * Hosted UI callback is always a full page load, so this is its query string.
 */
const initialUrl = typeof window !== 'undefined' ? { path: window.location.pathname, search: window.location.search } : { path: '', search: '' };

/** The callback's parameters, uncased, when this page load is the callback; otherwise whatever the URL has now. */
export function callbackParams(): URLSearchParams {
  const fromInitialLoad = initialUrl.path.toLowerCase().endsWith('/auth/redirect');
  return new URLSearchParams(fromInitialLoad ? initialUrl.search : window.location.search);
}

/** Only same-app paths, so `state` can't send an admin off-site. */
export function safeReturnPath(state: string | null): string {
  return state && state.startsWith('/') && !state.startsWith('//') ? state : '/';
}

/**
 * `auth/redirect` — where Cognito's Hosted UI returns after Google, Facebook
 * or Apple sign-in (`?code=…&state=<returnto>`, or `?error=…`). Exchanges the
 * code, then applies the same admin-only check as password sign-in.
 *
 * Reads the query string as first loaded (see `initialUrl`) rather than from
 * the router, so the code reaches Cognito exactly as issued.
 */
@Component({
  selector: 'admin-auth-redirect',
  standalone: true,
  imports: [IonContent, IonSpinner],
  template: `
    <ion-content class="ion-padding">
      <div class="signing-in"><ion-spinner name="dots"></ion-spinner><p>Signing you in…</p></div>
    </ion-content>
  `,
  styles: ['.signing-in { margin: 20vh auto 0; text-align: center; color: var(--ion-color-medium); }'],
})
export class AdminAuthRedirectComponent implements OnInit {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly toasts = inject(ToastService);
  private readonly platformId = inject(PLATFORM_ID);

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) void this.handle(callbackParams());
  }

  async handle(params: URLSearchParams): Promise<void> {
    const error = params.get('error');
    const code = params.get('code');
    if (error || !code) {
      if (error) this.toasts.error(params.get('error_description') || 'Sign-in was cancelled or is not available.');
      await this.router.navigate(['/auth/login']);
      return;
    }
    if (exchanged.has(code)) return;
    exchanged.add(code);

    try {
      await this.auth.completeFederatedSignIn(code);
      const user = await this.auth.currentUserOnceLoaded();
      if (!user) throw new Error('No session after sign-in');
      if (user.role !== Role.admin) {
        await this.auth.signOut('');
        await this.router.navigate(['/auth/login'], { queryParams: { denied: 1 } });
        return;
      }
      await this.router.navigateByUrl(safeReturnPath(params.get('state')));
    } catch {
      this.toasts.error('Sign-in did not complete. Please try again.');
      await this.router.navigate(['/auth/login']);
    }
  }
}
